<template>
    <div
        :class="['navbar__menu-item-wrapper', {'navbar__menu-item-wrapper--close-submenu': isSubmenuClosed}]"
        :data-testid="`navbar-menu-item-${index}`"
        @mouseenter="isSubmenuClosed = false"
    >
        <Component
            :is="menu.href ? NuxtLink : 'div'"
            v-bind="menu.href ? {to: $withLocalePrefix(menu.href), exactActiveClass: 'navbar__menu-item--active'} : {}"
            :class="['navbar__menu-item', {'navbar__menu-item--clickable': menu.href}]"
            @click="onHandleClick"
        >
            <div class="navbar__menu-item-label-wrapper">
                <div name="navbar-menu-item-label">{{ menu.label }}</div>
                <div name="navbar-menu-item-submenu-labels" class="navbar__menu-item-submenu-labels">
                    {{ submenuLabels }}
                </div>
            </div>
            <div class="navbar__menu-item-icon-wrapper">
                <VJoyIcon name="arrow-right" color="neutral-50" />
            </div>
        </Component>

        <NavbarSubmenu
            v-if="menu.items"
            :sections="menu.items"
            :feature-cards="menu.section"
            :is-open="isOpen"
            :parent-index="index"
            @close-menu="closeSubmenu"
        />
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {NavbarItem} from '@navbar-unlogged-types';
    import {NuxtLink} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import NavbarSubmenu from './NavbarSubmenu.vue';

    const props = defineProps<{
        menu: NavbarItem;
        index: number;
        isOpen: boolean;
    }>();

    const emits = defineEmits<{
        click: [];
        'close-menu': [];
    }>();

    const submenuLabels = computed(() => props.menu.items?.flatMap((section) => section.items?.map((item) => item.label) ?? []).join(', '));

    const isSubmenuClosed = ref(false);
    function closeSubmenu() {
        isSubmenuClosed.value = true;
        emits('close-menu');
    }

    function onHandleClick() {
        if (!props.menu.href) {
            emits('click');
            return;
        }

        closeSubmenu();
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    /* mobile-first */
    .navbar__menu-item-wrapper {
        height: 100%;
    }

    .navbar__menu-item-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }
    .navbar__menu-item {
        @include text.getFontBySize(primary-650);
        font-weight: var(--joy-font-weight-bold);
        letter-spacing: -0.2px;
        display: flex;
        justify-content: space-between;
        color: var(--joy-color-neutral-70);
        text-decoration: none;
        cursor: pointer;
        user-select: none;
        gap: var(--joy-core-spacing-3);

        &--active {
            color: var(--joy-color-neutral-40);
        }

        span,
        &-icon-wrapper {
            display: block;
            align-self: center;
        }

        &-submenu-labels {
            @include text.getFontBySize(primary-300);
            font-weight: 400;
            color: var(--joy-color-neutral-40);
            margin-top: var(--joy-core-spacing-1);
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    }

    /* desktop */
    @include mq.screen_sm(min) {
        .navbar__menu-item-wrapper {
            &:hover {
                .navbar__submenu {
                    display: flex;
                }
            }

            &--close-submenu:hover .navbar__submenu {
                display: none;
            }
        }

        .navbar__menu-item {
            @include text.getFontBySize(primary-400);
            letter-spacing: -0.2px;
            font-weight: 400;
            display: flex;
            color: var(--joy-color-neutral-60);
            text-decoration: none;
            padding: 0 var(--joy-core-spacing-2);
            white-space: nowrap;
            position: relative;
            user-select: none;
            cursor: default;
            height: 100%;
            align-items: flex-end;
            justify-content: center;
            pointer-events: none;

            &--clickable {
                cursor: pointer;
                pointer-events: auto;
            }

            &--active &-label-wrapper::before,
            &-label-wrapper:hover::before {
                content: '';
                position: absolute;
                bottom: -1px;
                left: 10px;
                right: 10px;
                height: 2px;
                background-color: var(--joy-color-neutral-60);
                z-index: 1;
            }

            &-label-wrapper {
                display: flex;
                height: 100%;
                align-items: center;
            }

            &-icon-wrapper,
            &-submenu-labels {
                display: none;
            }
        }
    }
</style>
